/** Pure helpers for auth routing decisions (AuthGuard + signin redirect). */

const PAY_PATH_PREFIX = "/pay/"
const PORTAL_PATH_PREFIX = "/portal/"
const SIGNIN_PATH = "/signin"
// Magic-link customer pages gated by a signed URL token, not a Django
// session: without this the ``/api/auth/me/`` probe 401s and strands the
// customer on the staff ``/signin`` (audit #15). ``/installments/failed``
// is a staff page and deliberately NOT public.
const CUSTOMER_INSTALLMENT_PATHS = new Set([
  "/installments/update-card",
  "/installments/reauth",
])

/** Pathnames the ``AuthGuard`` lets through without probing the session. */
export function isPublicRoute(pathname: string): boolean {
  if (pathname === SIGNIN_PATH || pathname === `${SIGNIN_PATH}/`) {
    return true
  }
  if (pathname === "/pay" || pathname.startsWith(PAY_PATH_PREFIX)) {
    return true
  }
  // The customer portal authenticates with its own ``billdozer_portal``
  // cookie, never the staff session, so the whole subtree is public.
  if (pathname === "/portal" || pathname.startsWith(PORTAL_PATH_PREFIX)) {
    return true
  }
  const withoutTrailingSlash = pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname
  if (CUSTOMER_INSTALLMENT_PATHS.has(withoutTrailingSlash)) {
    return true
  }
  return false
}

/**
 * Open-redirect guard for ``?next=``: returns the input when it is a
 * same-origin path, ``null`` otherwise. Rejects protocol-relative
 * ``//evil.com``, schemes, CR/LF (response splitting) and ``/signin``
 * itself (would loop after sign-in).
 */
export function sanitizeNextPath(raw: string | null): string | null {
  if (!raw) {
    return null
  }
  if (!raw.startsWith("/")) {
    return null
  }
  if (raw.startsWith("//")) {
    return null
  }
  if (/[\r\n]/.test(raw)) {
    return null
  }
  if (raw === SIGNIN_PATH || raw.startsWith(`${SIGNIN_PATH}?`) || raw.startsWith(`${SIGNIN_PATH}/`)) {
    return null
  }
  return raw
}
